// BeginButton.tsx - Begin Navigation Button Component
//
// This component renders the circular begin button that navigates users to the next step
// based on their onboarding status. It features a pulsing animation and accessibility support.
//
// Features:
// - Navigation based on user's onboarding status
// - Pulsing animation effect with custom styling
// - Accessible design with proper ARIA labels and keyboard support
// - Responsive circular design with semantic Tailwind classes
// - Layout-agnostic design (positioning handled by parent)
//
// Usage:
// <div className="absolute bottom-[25%] left-1/2 -translate-x-1/2">
//   <BeginButton handle={handle} />
// </div>

import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Handle } from "../resources/types/authTypes";

interface BeginButtonProps {
  handle: Handle | null;
  className?: string;
}

const BeginButton: React.FC<BeginButtonProps> = ({
  handle,
  className = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Handle navigation based on onboarding status
  const handleNavigation = useCallback(() => {
    if (handle && !handle.user.first_time_status) {
      navigate("/sangati", { replace: true });
    } else {
      navigate("/choose");
    }
  }, [handle, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleNavigation();
      }
    },
    [handleNavigation],
  );

  return (
    <div
      className={`
        relative
        w-36 h-36 sm:w-40 sm:h-40 md:w-44 md:h-44
        rounded-full
        bg-black/20
        flex items-center justify-center
        ${className}
      `}
    >
      {/* Top border accent */}
      <div className="absolute -top-0 left-1/2 -translate-x-1/2 w-2 h-0.5 bg-[#efede4]" />

      {/* Bottom border accent */}
      <div className="absolute -bottom-0 left-1/2 -translate-x-1/2 w-2 h-0.5 bg-[#efede4]" />

      {/* Inner circle container */}
      <div className="w-[calc(100%-2px)] h-[calc(100%-2px)] rounded-full bg-[#EFEDE4] flex items-center justify-center">
        <div className="relative flex items-center justify-center">
          {/* Pulsing animation circle */}
          <div
            className="
            absolute
            w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32
            rounded-full
            bg-sangati-green
            border-2 border-sangati-orange
            custom-ping
            z-10
          "
          />

          {/* Main button */}
          <button
            id="landing-begin-btn"
            onClick={handleNavigation}
            onKeyDown={handleKeyDown}
            className="
              relative z-20
              w-[120px] h-[120px] sm:w-32 sm:h-32 md:w-36 md:h-36
              rounded-full
              bg-sangati-green
              border-2 border-sangati-orange
              flex items-center justify-center
              font-bold text-white
              text-lg sm:text-xl md:text-2xl
              uppercase
              cursor-pointer
              focus:outline-none focus:ring-2 focus:ring-sangati-orange focus:ring-offset-2
              hover:scale-105 active:scale-95
              transition-transform duration-200
            "
            aria-label={t("landingButton")}
            tabIndex={0}
          />
        </div>
      </div>
    </div>
  );
};

export default BeginButton;
